import { FeatureFlags, LoginInput, User } from "@finway-group/shared/lib/models"
import { Col, Row } from "antd"
// Components' imports
import { LoginForm } from "Components/forms/loginForm"
import Icon from "Components/icon"
import { Location } from "history"
import * as HttpStatus from "http-status"
import React, { useEffect } from "react"
import { useCookies } from "react-cookie"
import { isMobile } from "react-device-detect"
// Other dependencies
import { useTranslation } from "react-i18next"
// Redux imports
import { useDispatch, useSelector } from "react-redux"
import { StaticContext, useHistory } from "react-router"
import { RouteComponentProps, withRouter } from "react-router-dom"
import { LocalStorageService } from "Shared/services"
// Model imports
// Shared modules imports
import NotificationService, { NotificationTypeEnum } from "Shared/services/notification.service"
import { ThunkDispatchResult } from "Shared/store"
// Store imports
import { login } from "Shared/store/actions/auth/authActions"
import { fetchAllCompanies } from "Shared/store/actions/company/companyActions"
import { setFeatureFlags } from "Shared/store/actions/featureFlags/featureFlagsActions"
import { fetchApiVersion } from "Shared/store/actions/fetcher/fetcherActions"
import { RootState } from "Shared/store/rootState.interface"
import { createCompanyIdCookieName } from "Shared/utils/cookie.utils"
import { isDev, msToTime } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import AuthFooter from "./auth.footer"
import LoginLogo from "./login.logo"
import TfAuthContainer from "./tfAuth.container"

export interface ILocationState {
    from: Location
}

interface LoginInterface {
    isAuthenticated?: boolean
    user?: User
}

type Props = LoginInterface & RouteComponentProps<{}, StaticContext, ILocationState>

const Login: React.FC<Props> = (props) => {
    const history = useHistory()
    const { t } = useTranslation()

    const [cookies, setCookie] = useCookies()

    const [loading, setLoading] = useStateIfMounted(false)
    const [isTfAuthVisible, setIsTfAuthVisible] = useStateIfMounted(false)
    const [loginData, setLoginData] = useStateIfMounted<LoginInput>({} as LoginInput)
    const [attemptCount, setAttemptCount] = useStateIfMounted(0)
    const [retryAfter, setRetryAfter] = useStateIfMounted(0)
    const [isLoginError, setIsLoginError] = useStateIfMounted(false)

    const dispatch: ThunkDispatchResult = useDispatch()

    const user = useSelector((state: RootState) => state.auth.user)
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)

    // redirect to / if the user is already logged in
    useEffect(() => {
        if (isAuthenticated) {
            const destination = user?.passwordExpired ? "/new-password" : "/"
            history.push(destination)
        }
    }, [])

    const redirect = (user: User): string => {
        let redirectTo: string
        if (user.passwordExpired) {
            redirectTo = `/new-password`
        } else {
            const { from } = props.location.state || { from: { pathname: "/" } }
            redirectTo = from.pathname
        }
        return redirectTo
    }

    /**
     * * Cache the previously used companyId, so the user will not have to reselect the latest company every time
     */
    useEffect(() => {
        const companyIdCookieValue = cookies[createCompanyIdCookieName(user)]
        companyIdCookieValue && companyIdCookieValue !== "" && LocalStorageService.setCompanyId(companyIdCookieValue)
    }, [])

    /**
     * * When a user login while having a companyId from other profile, we should update the companyId from the API
     */
    const shouldSetNewCompanyId = (user: User, companyId: string) => {
        const newCompanyIdCookieName = createCompanyIdCookieName(user)

        if (cookies[newCompanyIdCookieName]) {
            const userCompanyProfile = user.getUserCompanyProfile(cookies[newCompanyIdCookieName].toString())
            if (userCompanyProfile && userCompanyProfile.companyId && !userCompanyProfile.deleted) return
        }

        setCookie(newCompanyIdCookieName, companyId, { secure: !isDev, sameSite: "lax", path: "/", ...(!isDev && { domain: ".finway.de" }) })
    }

    const handleLogin = async (loginInput: LoginInput) => {
        try {
            setLoading(true)
            const [user, companyId, rawFeatureFlags] = await dispatch(login(loginInput))
            const featureFlags = new FeatureFlags(rawFeatureFlags)
            shouldSetNewCompanyId(user, companyId)
            await dispatch(fetchApiVersion())
            await dispatch(setFeatureFlags(featureFlags.getFlagsObject()))
            await dispatch(fetchAllCompanies())
            history.push(redirect(user))
            NotificationService.clear()
        } catch ({ response }) {
            setLoading(false)
            setAttemptCount(0)
            setRetryAfter(0)

            if (response) {
                const { status, data, headers } = response
                switch (status) {
                    case HttpStatus.PARTIAL_CONTENT:
                        setLoginData(loginInput)
                        setIsTfAuthVisible(true)
                        break
                    case HttpStatus.UNAUTHORIZED:
                        const attemptCounter = headers["retry-attempt"] || 0
                        setAttemptCount(Number(attemptCounter))

                        if (!headers["retry-attempt"]) {
                            setIsLoginError(true)
                        }

                        break
                    case HttpStatus.TOO_MANY_REQUESTS:
                        setRetryAfter(headers["retry-after"])
                        setAttemptCount(Number(headers["retry-attempt"]))
                        break

                    case HttpStatus.CONFLICT:
                        NotificationService.send(NotificationTypeEnum.ERROR, t("error:login.title"), t("error:login.clear_cookies"))
                        LocalStorageService.unsetAccessToken()
                        break

                    default:
                        if (data && data.error?.includes("suspended")) {
                            NotificationService.send(NotificationTypeEnum.ERROR, t("error:login.title"), t("error:login.message_forbidden"))
                            return
                        }

                        NotificationService.showErrorNotificationBasedOnResponseError(data.error, t("error:login.title"))
                        break
                }
            }
        }
    }

    const onTokenValidation = async (token: string) => {
        handleLogin({ ...loginData, token } as LoginInput)
        setIsTfAuthVisible(false)
    }

    const loginRateLimitingErrorView = (attemptCount > 0 || retryAfter > 1 || isLoginError) && (
        <div className="finway-error-bg border-l-4 border-error small">
            <div className="flex items-center">
                <div className="p-8 ml-20 mr-10 rounded-full border-1 border-error bg-white">
                    <Icon icon="login-error-lock" />
                </div>
                <div className="flex-col p-8 mr-10">
                    <h3 className="finway-error-login-text font-medium text-xs sm:text-base mb-0">
                        {attemptCount > 0 || isLoginError ? t("label:login_error.error") : t("label:login_error.account_blocked")}
                    </h3>
                    <p className="finway-error-login-text font-medium text-xxs sm:text-xs">
                        {attemptCount > 0 ? (
                            <>
                                <span className="font-bold finway-error-login-text">{attemptCount}</span> {t("label:login_error.remaining_attempt")}
                            </>
                        ) : (
                            !isLoginError && (
                                <>
                                    {t("label:login_error.retry_time")} {msToTime(retryAfter)}
                                </>
                            )
                        )}
                    </p>
                </div>
            </div>
        </div>
    )

    const scrollTo = (ref: any) => {
        if (isMobile && ref) {
            ref.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }

    const loginView = (
        <div className="login-bg flex flex-col items-center justify-center min-h-screen h-full w-full overflow-hidden relative">
            <Row className="w-full p-10 z-50 mb-0 xs:mb-30 my-auto" ref={scrollTo}>
                <Col span={24} className="max-w-400 min-w-300 my-0 mx-auto overflow-x-hidden overflow-y-hidden">
                    <LoginLogo />
                    <div className="bg-white w-full rounded-lg shadow mt-32 md:mt-40 pt-16">
                        {loginRateLimitingErrorView}

                        <div className="p-32">
                            <LoginForm onSubmit={handleLogin} loading={loading} attemptCount={attemptCount} />
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="w-full p-10 z-50 bottom-0 mb-0 sticky mt-auto">
                <Col span={2} className="my-0">
                    <div className="w-full flex items-center justify-center">
                        <p className="meta-long text-white text-sm sm:mr-0 ">version {require("../../../package.json").version}</p>
                    </div>
                </Col>
                <Col span={20} className="max-w-450 min-w-300 my-0 mx-auto">
                    <AuthFooter />
                </Col>
                {/* just to help center the middle column context */}
                <Col span={2}></Col>
            </Row>
        </div>
    )

    return <>{!isTfAuthVisible ? loginView : <TfAuthContainer tokenValidation={onTokenValidation} />}</>
}

export default withRouter(Login)
